import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { getPerformance } from "firebase/performance";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getStorage, connectStorageEmulator } from "firebase/storage";

// Validate required environment variables
const requiredEnvVars = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_STORAGE_BUCKET',
  'VITE_FIREBASE_MESSAGING_SENDER_ID',
  'VITE_FIREBASE_APP_ID'
] as const;

const missingEnvVars = requiredEnvVars.filter(envVar => !import.meta.env[envVar]);
if (missingEnvVars.length > 0) {
  throw new Error(`Missing required environment variables: ${missingEnvVars.join(', ')}`);
}

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

const app = initializeApp(firebaseConfig);

export const isEmulatorMode = import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true';

export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

if (isEmulatorMode) {
  connectAuthEmulator(auth, "http://localhost:9099");
  connectFirestoreEmulator(db, "localhost", 8080);
  connectStorageEmulator(storage, "localhost", 9199);
} else {
  getAnalytics(app);
  getPerformance(app);
}

// Utility function to replace undefined with null
export function replaceUndefinedWithNull<T extends Record<string, any>>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      if (value === undefined) {
        return [key, null];  // Replace undefined with null
      } else if (Array.isArray(value)) {
        // Process array but do not convert it to an object
        return [key, value.map(item => typeof item === 'object' && item !== null ? replaceUndefinedWithNull(item) : item)];
      } else if (value instanceof Map) {
        // Process Map separately
        return [key, new Map(Array.from(value.entries()).map(([k, v]) => [k, typeof v === 'object' && v !== null ? replaceUndefinedWithNull(v) : v]))];
      } else if (typeof value === 'object' && value !== null) {
        // Recursively process nested objects
        return [key, replaceUndefinedWithNull(value)];
      } else {
        return [key, value];  // Keep other values as-is
      }
    })
  ) as T;
}
